import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Loader2, Plus, Trash2, Copy, Check } from "lucide-react";
import {
  deleteQualificationQuestion,
  listQualificationQuestions,
  QUALIFICATION_CATEGORIES,
  saveQualificationQuestion,
  type QualificationQuestion,
} from "@/lib/admin-qualification.functions";

export const Route = createFileRoute("/_authenticated/admin/saljfragor")({
  head: () => ({
    meta: [
      { title: "Säljfrågor — Fenny admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: QualificationPage,
});

const SOURCES = [
  { value: "", label: "Alla formulär" },
  { value: "contact", label: "Kontakt" },
  { value: "quote", label: "Offert / pris" },
  { value: "foto", label: "Foto" },
  { value: "callback", label: "Uppringning" },
  { value: "prime", label: "Prime" },
  { value: "partner", label: "Partner" },
  { value: "lead_magnet", label: "Formatguide" },
  { value: "newsletter", label: "Nyhetsbrev" },
  { value: "career", label: "Karriär" },
];

type Draft = {
  id: string | null;
  category: string;
  question: string;
  answer: string;
  follow_up: string;
  lead_source: string;
  sort_index: number;
  active: boolean;
};

const emptyDraft = (): Draft => ({
  id: null,
  category: "behov",
  question: "",
  answer: "",
  follow_up: "",
  lead_source: "",
  sort_index: 0,
  active: true,
});

const catLabel = (v: string) =>
  QUALIFICATION_CATEGORIES.find((c) => c.value === v)?.label ?? v;

function QualificationPage() {
  const listFn = useServerFn(listQualificationQuestions);
  const saveFn = useServerFn(saveQualificationQuestion);
  const delFn = useServerFn(deleteQualificationQuestion);
  const qc = useQueryClient();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const [copied, setCopied] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["admin", "qualification"],
    queryFn: () => listFn(),
  });

  const save = useMutation({
    mutationFn: (d: Draft) =>
      saveFn({
        data: {
          id: d.id ?? undefined,
          category: d.category,
          question: d.question,
          answer: d.answer,
          follow_up: d.follow_up,
          lead_source: d.lead_source,
          sort_index: d.sort_index,
          active: d.active,
        },
      }),
    onSuccess: () => {
      setDraft(null);
      qc.invalidateQueries({ queryKey: ["admin", "qualification"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "qualification"] }),
  });

  const items = list.data?.items ?? [];
  const visible = useMemo(
    () => (filter === "all" ? items : items.filter((i) => i.category === filter)),
    [items, filter],
  );

  function copyScript() {
    const text = visible
      .filter((i) => i.active)
      .map(
        (i, idx) =>
          `${idx + 1}. ${i.question}${i.answer ? `\n   Svar: ${i.answer}` : ""}${
            i.follow_up ? `\n   Följdfråga: ${i.follow_up}` : ""
          }`,
      )
      .join("\n\n");
    void navigator.clipboard.writeText(text);
    setCopied("script");
    setTimeout(() => setCopied(null), 1500);
  }

  return (
    <div className="space-y-6 p-5 md:p-8">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold">Säljfrågor</h1>
        <p className="max-w-2xl text-sm text-white/50">
          Frågorna och svaren ni använder när ett lead ska bli ett bokat
          säljsamtal. Skriv frågan, vad ni vill höra och en följdfråga — så låter
          alla i teamet lika oavsett vem som ringer.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setFilter("all")}
          className={`rounded-full px-3 py-1.5 text-xs ${filter === "all" ? "bg-mint text-mint-foreground" : "bg-white/5 text-white/70"}`}
        >
          Alla ({items.length})
        </button>
        {QUALIFICATION_CATEGORIES.map((c) => {
          const n = items.filter((i) => i.category === c.value).length;
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => setFilter(c.value)}
              className={`rounded-full px-3 py-1.5 text-xs ${filter === c.value ? "bg-mint text-mint-foreground" : "bg-white/5 text-white/70"}`}
            >
              {c.label} ({n})
            </button>
          );
        })}
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={copyScript}
            className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1.5 text-xs text-white/70 hover:bg-white/10"
          >
            {copied === "script" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            Kopiera samtalsmall
          </button>
          <button
            type="button"
            onClick={() => setDraft(emptyDraft())}
            className="inline-flex items-center gap-2 rounded-full bg-mint px-3 py-1.5 text-xs font-semibold text-mint-foreground"
          >
            <Plus className="h-3.5 w-3.5" /> Ny fråga
          </button>
        </div>
      </div>

      {draft && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(draft);
          }}
          className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5"
        >
          <div className="grid gap-4 md:grid-cols-3">
            <label className="text-xs text-white/60">
              Kategori
              <select
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
              >
                {QUALIFICATION_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </label>
            <label className="text-xs text-white/60">
              Gäller lead från
              <select
                value={draft.lead_source}
                onChange={(e) => setDraft({ ...draft, lead_source: e.target.value })}
                className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
              >
                {SOURCES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </label>
            <label className="text-xs text-white/60">
              Ordning
              <input
                type="number"
                value={draft.sort_index}
                onChange={(e) => setDraft({ ...draft, sort_index: Number(e.target.value) || 0 })}
                className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
              />
            </label>
          </div>

          <label className="block text-xs text-white/60">
            Fråga
            <input
              value={draft.question}
              onChange={(e) => setDraft({ ...draft, question: e.target.value })}
              placeholder="Vad ska filmen få att hända hos er?"
              className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
              required
            />
          </label>

          <label className="block text-xs text-white/60">
            Det här vill vi höra / vårt svar
            <textarea
              value={draft.answer}
              onChange={(e) => setDraft({ ...draft, answer: e.target.value })}
              rows={3}
              placeholder="Konkret mål (fler leads, snabbare rekrytering…). Saknas mål — föreslå ett kort strategisamtal."
              className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
            />
          </label>

          <label className="block text-xs text-white/60">
            Följdfråga
            <input
              value={draft.follow_up}
              onChange={(e) => setDraft({ ...draft, follow_up: e.target.value })}
              placeholder="Hur mäter ni det idag?"
              className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white"
            />
          </label>

          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-white/60">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
              />
              Aktiv i samtalsmallen
            </label>
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={() => setDraft(null)}
                className="rounded-full bg-white/5 px-4 py-2 text-xs text-white/70"
              >
                Avbryt
              </button>
              <button
                type="submit"
                disabled={save.isPending}
                className="inline-flex items-center gap-2 rounded-full bg-mint px-4 py-2 text-xs font-semibold text-mint-foreground disabled:opacity-60"
              >
                {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Spara
              </button>
            </div>
          </div>
          {save.isError && (
            <p className="text-xs text-red-400">Kunde inte spara. Försök igen.</p>
          )}
        </form>
      )}

      {list.isLoading && (
        <p className="text-sm text-white/50">Hämtar frågor…</p>
      )}

      {!list.isLoading && visible.length === 0 && (
        <p className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-sm text-white/50">
          Inga frågor än. Skapa den första så teamet har något att luta sig mot i
          första samtalet.
        </p>
      )}

      <ul className="space-y-3">
        {visible.map((q: QualificationQuestion) => (
          <li
            key={q.id}
            className={`rounded-2xl border border-white/10 bg-white/[0.03] p-4 ${q.active ? "" : "opacity-50"}`}
          >
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/45">
                  <span className="rounded-full bg-white/5 px-2 py-0.5">{catLabel(q.category)}</span>
                  {q.lead_source && (
                    <span className="rounded-full bg-white/5 px-2 py-0.5">
                      {SOURCES.find((s) => s.value === q.lead_source)?.label ?? q.lead_source}
                    </span>
                  )}
                  {!q.active && <span>Pausad</span>}
                </div>
                <p className="mt-2 text-sm font-medium text-white">{q.question}</p>
                {q.answer && (
                  <p className="mt-1 whitespace-pre-wrap text-sm text-white/60">{q.answer}</p>
                )}
                {q.follow_up && (
                  <p className="mt-1 text-xs text-white/45">Följdfråga: {q.follow_up}</p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setDraft({
                      id: q.id,
                      category: q.category,
                      question: q.question,
                      answer: q.answer,
                      follow_up: q.follow_up ?? "",
                      lead_source: q.lead_source ?? "",
                      sort_index: q.sort_index,
                      active: q.active,
                    })
                  }
                  className="rounded-full bg-white/5 px-3 py-1.5 text-xs text-white/70 hover:bg-white/10"
                >
                  Redigera
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm("Ta bort frågan?")) remove.mutate(q.id);
                  }}
                  className="rounded-full bg-white/5 p-2 text-white/50 hover:bg-red-500/15 hover:text-red-300"
                  aria-label="Ta bort"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
